import { type ListSystemRolesQuery, type SystemRole } from '@clockwise/clients/system-roles';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Filter,
  Pencil,
  Plus,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import { useDuplicateSystemRole } from '@/components/custom/duplicate-system-role';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { systemRolesApi } from '@/lib/api';
import { useCanIn, useUnitNames, useUnitsWhere } from '@/lib/business-units';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['name', 'createdAt', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  /** A Business Unit's id. */
  unit: z.string().optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/system-roles/')({
  validateSearch: listSearch,
  component: SystemRolesPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';

function SystemRolesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const unit = search.unit;
  // The units whose roles they see (the column and filter), and those where
  // their role may change roles (the row actions, and where "Add role" goes).
  const businessUnits = useUnitsWhere('system_role.view');
  const addUnits = useUnitsWhere('system_role.edit');
  const canIn = useCanIn();
  const unitNames = useUnitNames();
  // Where "Add role" starts: the unit picked above (if they may add there),
  // else the only unit they may add to. With several, the sheet asks which.
  const createIn =
    unit && addUnits.some((u) => u.id === unit)
      ? unit
      : addUnits.length === 1
        ? addUnits[0]?.id
        : undefined;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // One sheet serves both add and edit — `editing` null means create.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<SystemRole | null>(null);
  const queryClient = useQueryClient();
  const duplicate = useDuplicateSystemRole();

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListSystemRolesQuery = {
    q: q.trim() || undefined,
    ...(unit ? { businessUnitId: unit } : {}),
    active: status === 'all' ? undefined : status === 'active',
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.systemRoles.list(listQuery),
    queryFn: () => systemRolesApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => systemRolesApi.remove(id),
    onSuccess: () => {
      toast.success('Role deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.systemRoles.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Delete failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => systemRolesApi.restore(id),
    onSuccess: () => {
      toast.success('Role reactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.systemRoles.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<SystemRole>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to="/setup/system-roles/$id"
            params={{ id: row.original.id }}
            className="font-medium text-primary underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
            {!row.original.active ? (
              <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </Link>
        ),
      },
      // Its Business Unit — shown once the caller works in more than one.
      ...(businessUnits.length > 1
        ? [
            {
              id: 'businessUnit',
              header: 'Business Unit',
              enableSorting: false,
              cell: ({ row }) =>
                unitNames.get(row.original.businessUnitId) ?? (
                  <span className="text-muted-foreground">—</span>
                ),
            } satisfies ColumnDef<SystemRole>,
          ]
        : []),
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ?? <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        // Only where their role in THIS role's unit may change roles.
        cell: ({ row }) =>
          canIn('system_role.edit', row.original.businessUnitId) ? (
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row.original);
                  setSheetOpen(true);
                }}
              >
                <Pencil />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Duplicate"
                title="Duplicate"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicate.start(row.original.id);
                }}
                disabled={duplicate.pending}
              >
                <Copy />
              </Button>
              {row.original.active ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Deactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Deactivate "${row.original.name}"?`)) {
                      deleteMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Reactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RowActions>
          ) : null,
      },
    ],
    [
      deleteMutation,
      restoreMutation,
      unitNames,
      businessUnits.length,
      canIn,
      duplicate.start,
      duplicate.pending,
    ],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="System roles"
        subtitle="In-app permission groupings (Administrator, Finance, …)."
        count={total}
      >
        <RequirePermission permission="system_role.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add role
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name, description…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        {businessUnits.length > 1 ? (
          <Select
            value={unit ?? 'all'}
            onValueChange={(v) => updateSearch({ unit: v === 'all' ? undefined : v, page: 0 })}
          >
            <SelectTrigger className="w-52" aria-label="Business Unit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Business Units</SelectItem>
              {businessUnits.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError ? query.error.message : 'Failed to load roles.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q ? 'No roles match this search.' : 'No system roles yet. Click “Add role”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/setup/system-roles/$id',
                      params: { id: r.original.id },
                    })
                  }
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      {duplicate.sheet}

      <CatalogFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        entityLabel="system role"
        entity={editing}
        permissionsEditable
        uniqueWithin="its Business Unit"
        // Adding: picked (starting from the filter); editing: shown, fixed.
        unitField={{
          defaultUnitId: createIn,
          current: editing?.businessUnitId,
          view: 'system_role.view',
          add: 'system_role.edit',
        }}
        submit={(body) => {
          const payload = {
            name: body.name,
            description: body.description,
            permissions: body.permissions ?? [],
          };
          if (editing) return systemRolesApi.patch(editing.id, payload);
          const businessUnitId = body.businessUnitId ?? createIn;
          return systemRolesApi.create({
            ...payload,
            ...(businessUnitId ? { businessUnitId } : {}),
          });
        }}
        invalidateKeys={
          editing
            ? [
                queryKeys.systemRoles.all,
                queryKeys.audit.all,
                queryKeys.systemRoles.detail(editing.id),
              ]
            : [queryKeys.systemRoles.all, queryKeys.audit.all]
        }
      />
    </PageShell>
  );
}

const defaultCell: ColumnDef<SystemRole>['cell'] = ({ getValue }) => String(getValue() ?? '');

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  if (s.unit) out.unit = s.unit;
  return out;
}
